import React, { useState } from "react";
import { Check } from "lucide-react";
import DocSidebar from "../components/DocSidebar";
import CodeBlock from "../components/CodeBlock";
import MethodBadge from "../components/MethodBadge";
import Keyword from "../components/Keyword";

const sidebarItems = [
  { id: "st-overview-nav", label: "Getting Started", type: "category" },
  { id: "st-overview", label: "Overview", type: "item", indent: true },

  { id: "st-getting-started", label: "Getting Started", type: "heading" },
  { id: "st-base-url", label: "Base URL", type: "item", indent: true },
  { id: "st-auth-header", label: "Authentication Header", type: "item", indent: true },

  { id: "st-api", label: "Speech Translation API", type: "heading" },
  { id: "st-endpoint", label: "Endpoint (/with-api-key)", type: "item", indent: true },
  { id: "st-full-endpoint-url", label: "Full Endpoint URL", type: "item", indent: true },
  { id: "st-request-header", label: "Request Headers", type: "item", indent: true },
  { id: "st-request-body", label: "Request Body (Multipart Form)", type: "item", indent: true },

  { id: "st-language-reference", label: "Supported Pairs & Rules", type: "heading" },
  { id: "st-supported-pairs", label: "Supported Translation Pairs", type: "item", indent: true },

  { id: "st-code-examples", label: "Code Examples", type: "heading" },
  { id: "st-python", label: "Python", type: "item", indent: true },
  { id: "st-curl", label: "cURL", type: "item", indent: true },
  { id: "st-javascript", label: "JavaScript (Fetch)", type: "item", indent: true },

  { id: "st-response-format", label: "Response Format", type: "heading" },
  { id: "st-success-response", label: "Success Response", type: "item", indent: true },
  { id: "st-error-responses", label: "Error Responses", type: "item", indent: true },

  { id: "st-best-practices", label: "Best Practices", type: "heading" },
  { id: "st-next-steps", label: "Next Steps", type: "heading" },
];

const SpeechTranslationsTab = () => {
  const [activeSection, setActiveSection] = useState("st-overview");

  return (
    <div className="flex flex-col lg:flex-row gap-8 max-w-[1440px] mx-auto px-2 sm:px-6 py-6 sm:py-10 relative">
      {/* Sidebar Navigation */}
      <DocSidebar
        items={sidebarItems}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
      />

      {/* Main Content */}
      <div className="flex flex-col gap-16 flex-1 lg:max-w-none xl:px-10">
        {/* Header */}
        <section id="st-overview-nav" className="flex flex-col gap-4">
          <div className="inline-block">
            <span className="font-black text-sm uppercase tracking-[0.2em] text-[#17A257] bg-[#E7F6EC] px-3 py-1 rounded-full">
              Speech Translations API
            </span>
          </div>
          <div id="st-overview" className="flex flex-col gap-4">
            <h1 className="font-black text-3xl lg:text-4xl leading-tight text-[#1D2129]">
              Quickstart Guide For Speech Translations API
            </h1>
            <p className="font-medium text-lg leading-relaxed text-[#7E726B]">
              The NKENNE AI Speech Translations API enables direct cross-lingual translation of spoken audio into translated text using your developer API key. Convert spoken Swahili, Yoruba, or Nigerian Pidgin directly into English, or English audio into African languages in a single streamlined API call.
            </p>
          </div>
        </section>

        {/* Getting Started Section */}
        <div className="flex flex-col gap-20">
          <section id="st-getting-started" className="flex flex-col gap-6 w-full">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Getting Started
            </h2>

            <div className="flex flex-col gap-6">
              {/* Base URL */}
              <div id="st-base-url" className="flex flex-col gap-2">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Base URL
                </h3>
                <div className="bg-[#F4F8F7] p-3 rounded-xl border border-[#D0E7DF] inline-block w-fit">
                  <p className="font-mono font-medium text-base text-[#1E6C56]">
                    https://developers-api.nkenne.ai
                  </p>
                </div>
              </div>

              {/* Authentication Header */}
              <div id="st-auth-header" className="flex flex-col gap-3">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Authentication Header
                </h3>
                <p className="font-['Inter'] font-medium text-base text-[#7E726B]">
                  All speech translation requests require an API key in the <Keyword>Authorization</Keyword> header:
                </p>
                <div className="bg-[#1D2129] p-4 rounded-xl font-mono text-sm text-white overflow-hidden w-fit">
                  <span className="text-[#BC39B7]">Authorization</span>:{" "}
                  <span className="text-[#17A257]">YOUR_API_KEY</span>
                </div>
              </div>
            </div>
          </section>

          {/* Speech Translation API */}
          <section id="st-api" className="flex flex-col gap-6">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Speech Translation API
            </h2>

            <div className="flex flex-col gap-6">
              {/* Endpoint */}
              <div id="st-endpoint" className="flex flex-col gap-5">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Endpoint
                </h3>
                <div className="flex items-center gap-3">
                  <MethodBadge method="POST" />
                  <span className="font-mono text-sm font-semibold text-[#1D2129]">
                    /api/st/v1/{`{pair}`}/with-api-key
                  </span>
                </div>
              </div>

              {/* Full Endpoint URL */}
              <div id="st-full-endpoint-url" className="flex flex-col gap-3">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Full Endpoint URL
                </h3>
                <p className="font-mono text-sm font-medium text-[#1E6C56] bg-[#F4F8F7] p-3 rounded-xl border border-[#D0E7DF] inline-block w-fit">
                  https://developers-api.nkenne.ai/api/st/v1/{`{pair}`}/with-api-key
                </p>
              </div>

              {/* Request Headers */}
              <div id="st-request-header" className="flex flex-col gap-4">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Request Headers
                </h3>
                <div className="border border-[#E0E4E2] rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E0E4E2] bg-gray-50/50">
                        <th className="text-left p-3 font-['Axiforma'] font-semibold text-base text-[#000000]">
                          Header
                        </th>
                        <th className="text-left p-3 font-['Axiforma'] font-semibold text-base text-[#000000]">
                          Required
                        </th>
                        <th className="text-left p-3 font-['Axiforma'] font-semibold text-base text-[#000000]">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-[#E0E4E2]">
                        <td className="p-3">
                          <Keyword>Authorization</Keyword>
                        </td>
                        <td className="p-3">
                          <Check className="w-5 h-5 text-[#17A257]" />
                        </td>
                        <td className="p-3 text-sm text-[#000000]">
                          YOUR_API_KEY
                        </td>
                      </tr>
                      <tr>
                        <td className="p-3">
                          <Keyword>Content-Type</Keyword>
                        </td>
                        <td className="p-3">
                          <Check className="w-5 h-5 text-[#17A257]" />
                        </td>
                        <td className="p-3 text-sm">
                          <Keyword color="#0070C1">multipart/form-data</Keyword>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Request Body (Multipart) */}
              <div id="st-request-body" className="flex flex-col gap-4">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Request Body (Multipart Form-Data)
                </h3>
                <div className="border border-[#E0E4E2] rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E0E4E2] bg-gray-50/50">
                        <th className="text-left p-3 font-bold text-sm text-[#000000]">Field</th>
                        <th className="text-left p-3 font-bold text-sm text-[#000000]">Type</th>
                        <th className="text-left p-3 font-bold text-sm text-[#000000]">Required</th>
                        <th className="text-left p-3 font-bold text-sm text-[#000000]">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="p-3"><Keyword color="#BC39B7">audio_file</Keyword></td>
                        <td className="p-3 text-sm font-mono text-gray-600">File / Binary</td>
                        <td className="p-3"><Check className="w-5 h-5 text-[#17A257]" /></td>
                        <td className="p-3 text-sm">Spoken audio file to translate (max 25MB).</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>

          {/* Supported Translation Pairs */}
          <section id="st-language-reference" className="flex flex-col gap-6">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Supported Translation Pairs & Rules
            </h2>

            <div id="st-supported-pairs" className="flex flex-col gap-4">
              <div className="border border-[#E0E4E2] rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E0E4E2] bg-gray-50/50">
                      <th className="text-left p-3 font-bold text-sm text-[#000000]">Source Audio</th>
                      <th className="text-left p-3 font-bold text-sm text-[#000000]">Target Text</th>
                      <th className="text-left p-3 font-bold text-sm text-[#000000]">URL Pair Parameter</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { from: "Swahili", to: "English", pair: "swahili-to-english" },
                      { from: "Yoruba", to: "English", pair: "yoruba-to-english" },
                      { from: "Nigerian Pidgin", to: "English", pair: "pidgin-to-english" },
                      { from: "English", to: "Swahili", pair: "english-to-swahili" },
                      { from: "English", to: "Yoruba", pair: "english-to-yoruba" },
                      { from: "English", to: "Nigerian Pidgin", pair: "english-to-pidgin" },
                    ].map((row, index) => (
                      <tr key={index} className={index < 5 ? "border-b border-[#E0E4E2]" : ""}>
                        <td className="p-3 font-medium text-sm text-[#000000]">{row.from}</td>
                        <td className="p-3 font-medium text-sm text-[#000000]">{row.to}</td>
                        <td className="p-3"><Keyword color="#0070C1">{row.pair}</Keyword></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Code Examples */}
          <section id="st-code-examples" className="flex flex-col gap-8">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Code Examples
            </h2>

            <div className="flex flex-col gap-8">
              {/* Python Example */}
              <div id="st-python" className="flex flex-col gap-3">
                <p className="font-['Axiforma'] font-bold text-lg text-[#000000]">
                  Python
                </p>
                <CodeBlock
                  id="st-python-example"
                  language="python"
                  code={`import requests

API_URL = "https://developers-api.nkenne.ai/api/st/v1/swahili-to-english/with-api-key"
API_KEY = "your_api_key_here"

headers = {
    "Authorization": API_KEY
}

with open("swahili_speech.wav", "rb") as f:
    files = {"audio_file": ("swahili_speech.wav", f, "audio/wav")}
    response = requests.post(API_URL, headers=headers, files=files)

response.raise_for_status()
data = response.json()

print("Translated Text:", data.get("translated_text"))
print("Source Language:", data.get("source_language"))
print("Target Language:", data.get("target_language"))`}
                />
              </div>

              {/* cURL Example */}
              <div id="st-curl" className="flex flex-col gap-3">
                <p className="font-['Axiforma'] font-bold text-base text-[#000000]">
                  cURL
                </p>
                <CodeBlock
                  id="st-curl-example"
                  language="bash"
                  code={`curl -X POST https://developers-api.nkenne.ai/api/st/v1/swahili-to-english/with-api-key \\
  -H "Authorization: YOUR_API_KEY" \\
  -F "audio_file=@/path/to/swahili_speech.wav"`}
                />
              </div>

              {/* JavaScript Example */}
              <div id="st-javascript" className="flex flex-col gap-3">
                <p className="font-['Axiforma'] font-bold text-base text-[#000000]">
                  JavaScript (Fetch)
                </p>
                <CodeBlock
                  id="st-javascript-example"
                  language="javascript"
                  code={`const formData = new FormData();
formData.append("audio_file", audioFile);

const response = await fetch(
  "https://developers-api.nkenne.ai/api/st/v1/swahili-to-english/with-api-key",
  {
    method: "POST",
    headers: {
      "Authorization": "YOUR_API_KEY"
    },
    body: formData
  }
);

const data = await response.json();
console.log("Translated text:", data.translated_text);`}
                />
              </div>
            </div>
          </section>

          {/* Response Format */}
          <section id="st-response-format" className="flex flex-col gap-8">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Response Format
            </h2>

            <div className="flex flex-col gap-10">
              {/* Success Response */}
              <div id="st-success-response" className="flex flex-col gap-3">
                <h3 className="font-['Axiforma'] font-black text-lg text-[#000000]">
                  Success Response (200 OK)
                </h3>
                <CodeBlock
                  id="st-success-response-code"
                  language="json"
                  code={`{
  "translated_text": "Good morning everyone, how is everything going today?",
  "source_language": "swahili",
  "target_language": "english"
}`}
                />
              </div>

              {/* Error Responses */}
              <div id="st-error-responses" className="flex flex-col gap-6">
                <h3 className="font-['Axiforma'] font-black text-lg text-[#000000]">
                  Error Responses
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Invalid API Key */}
                  <div className="flex flex-col gap-3 p-5 rounded-2xl border border-red-100 bg-red-50/20">
                    <p className="font-bold text-sm text-[#E11D48]">
                      Unauthorized (401)
                    </p>
                    <CodeBlock
                      id="st-error-401"
                      language="json"
                      code={`{
  "error": "Unauthorized: Invalid API Key"
}`}
                    />
                    <p className="text-xs text-[#7E726B] leading-relaxed">
                      API key is missing, expired, or invalid. Verify your key in Developer Tools.
                    </p>
                  </div>

                  {/* Invalid Pair */}
                  <div className="flex flex-col gap-3 p-5 rounded-2xl border border-red-100 bg-red-50/20">
                    <p className="font-bold text-sm text-[#E11D48]">
                      Invalid Pair (400)
                    </p>
                    <CodeBlock
                      id="st-error-400"
                      language="json"
                      code={`{
  "error": "Unsupported translation pair slug"
}`}
                    />
                    <p className="text-xs text-[#7E726B] leading-relaxed">
                      Check that the translation pair matches supported URL slugs (e.g. swahili-to-english).
                    </p>
                  </div>

                  {/* File Size Exceeded */}
                  <div className="flex flex-col gap-3 p-5 rounded-2xl border border-red-100 bg-red-50/20">
                    <p className="font-bold text-sm text-[#E11D48]">
                      Payload Too Large (413)
                    </p>
                    <CodeBlock
                      id="st-error-413"
                      language="json"
                      code={`{
  "error": "Audio file exceeds maximum size limit (25MB)"
}`}
                    />
                    <p className="text-xs text-[#7E726B] leading-relaxed">
                      Ensure audio files are below the 25MB per-request limit.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Best Practices */}
          <section id="st-best-practices" className="flex flex-col gap-6">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Best Practices
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: "Direct Pipeline", desc: "Use Speech Translation instead of chaining STT + Text Translation for reduced latency and compounding error rates." },
                { title: "Audio Clarity", desc: "Ensure clear speaker speech for best cross-lingual contextual understanding." },
                { title: "Format Selection", desc: "Use WAV or compressed MP3 to minimize bandwidth while retaining audio fidelity." },
                { title: "Async Workflow", desc: "Handle longer speech translation tasks asynchronously to ensure smooth user interfaces." },
              ].map((practice, index) => (
                <div key={index} className="p-4 rounded-xl border border-[#E0E4E2] bg-white">
                  <h4 className="font-bold text-base text-[#1D2129] mb-1">{practice.title}</h4>
                  <p className="text-sm text-[#7E726B]">{practice.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Next Steps */}
          <section id="st-next-steps" className="flex flex-col gap-6 mb-12">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Next Steps
            </h2>
            <div className="flex flex-col gap-3">
              {[
                "Try out live speech-to-text translations in the Speech Studio",
                "Integrate multi-lingual voice interpretation in live communication tools",
                "Explore enterprise custom translation models tailored to your domain vocabulary",
              ].map((step, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#17A257]" />
                  <p className="font-medium text-base text-[#1D2129]">{step}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default SpeechTranslationsTab;
